import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Check, ClipboardList } from "lucide-react";
import { QRCodeSVG } from "qrcode.react";
import { useCheckInSelector } from "../context";
import { checkInSelectors } from "../selectors";
import { usePlatform } from "../../../../../platform/context";
import { frontdeskDiagnosticLogger } from "../../../../../lib/diagnostic-logger";
import { reportOperationalError } from "../../../../../lib/operational-error-telemetry";
import { useAudio } from "../../../../../lib/use-audio";
import { useTimedTerminal } from "../../../use-timed-terminal";
import { TimedTerminalFooter } from "../../../components/TimedTerminalFooter";
import { KioskStep } from "../../../../../components/kiosk-step";
import { KioskStatePanel } from "../../../../../components/kiosk-state-panel";

const AUTO_REDIRECT_SECONDS = 90;

interface DoneStepProps {
  onComplete?: () => void;
}

/**
 * Done step: check-in complete or the card-expired state, as one content-sized
 * state panel with the countdown beside the message and "Fertig" in the footer
 * (KC-25). Both keep the shared timed-terminal countdown.
 *
 * @since 1.0.0
 * @example
 * ```tsx
 * <DoneStep onComplete={() => undefined} />
 * ```
 */
export function DoneStep({ onComplete }: DoneStepProps) {
  const { t } = useTranslation();
  const platform = usePlatform();
  const cardExpired = useCheckInSelector(checkInSelectors.cardExpired);
  const patientInfoDoneMode = useCheckInSelector(
    checkInSelectors.patientInfoDoneMode,
  );

  const isSuccess = !cardExpired;

  // The closing screen names the journey the patient actually finished. Confirming, saving, and
  // skipping the contact-detail review are three different outcomes, and telling a patient who
  // skipped that their changes were processed would be untrue. Absent mode → the review never ran,
  // so the generic check-in copy is the honest default.
  const doneCopyKey =
    patientInfoDoneMode === undefined ? "" : `-${patientInfoDoneMode}`;
  // The generic title avoids the anglicism "Eingecheckt." (KC-08).
  const titleKey =
    doneCopyKey === ""
      ? "self-service.check-in.done.title-v2"
      : `self-service.check-in.done.title${doneCopyKey}`;
  const subtitleKey = `self-service.check-in.done.subtitle${doneCopyKey}`;

  // Digital anamnesis link/QR — configured per practice in settings, static for
  // all patients. Loaded from the platform store like other practice settings.
  const [anamnesisEnabled, setAnamnesisEnabled] = useState(false);
  const [anamnesisUrl, setAnamnesisUrl] = useState("");

  useEffect(
    function runLoadAnamnesisSettingsEffect() {
      let active = true;
      const loadAnamnesisSettings = async () => {
        try {
          const settings = await platform.getPracticeSettings();
          if (!active) return;
          setAnamnesisEnabled(settings.anamnesisEnabled ?? false);
          setAnamnesisUrl(settings.anamnesisUrl ?? "");
        } catch (error) {
          frontdeskDiagnosticLogger.error(
            "[FrontDesk+] Failed to load anamnesis settings on done step",
            { error },
          );
          void reportOperationalError({
            action: "check-in.done-settings-load",
            severity: "warning",
            source: "native",
            component: "DoneStep",
            error,
          });
        }
      };
      loadAnamnesisSettings();
      return function cancelLoadAnamnesisSettings() {
        active = false;
      };
    },
    [platform],
  );

  // Play audio on mount
  useAudio(isSuccess ? "checkIn-done" : undefined);

  // Shared timed terminal with "more time" and reassurance overlay
  const { countdown, hasExtended, extendTime } = useTimedTerminal({
    initialSeconds: AUTO_REDIRECT_SECONDS,
    onExpire: () => onComplete?.(),
  });

  const finishAction = {
    label: t("common.done"),
    icon: Check,
    tone: "success" as const,
    onClick: () => onComplete?.(),
    testId: "done-finish",
  };

  if (!isSuccess) {
    return (
      <KioskStep
        width="narrow"
        align="center"
        title={t("self-service.check-in.done.card-expired-title")}
        primaryAction={finishAction}
      >
        <KioskStatePanel
          tone="error"
          title={t("self-service.check-in.done.card-expired-subtitle")}
          body={t("self-service.check-in.done.card-expired-info-v2")}
        >
          <TimedTerminalFooter
            countdown={countdown}
            hasExtended={hasExtended}
            onExtendTime={extendTime}
          />
        </KioskStatePanel>
      </KioskStep>
    );
  }

  return (
    <KioskStep
      width="narrow"
      align="center"
      title={t(titleKey)}
      primaryAction={finishAction}
    >
      <KioskStatePanel tone="success" title={t(subtitleKey)} testId="frontdesk-check-in-done">
        <TimedTerminalFooter
          countdown={countdown}
          hasExtended={hasExtended}
          onExtendTime={extendTime}
        />
      </KioskStatePanel>

      {anamnesisEnabled && anamnesisUrl ? (
        <section className="mx-auto flex w-full max-w-xl min-w-0 flex-col items-center gap-4 rounded-3xl border bg-card p-6 text-center sm:flex-row sm:text-left">
          <div className="grid shrink-0 place-items-center rounded-2xl border bg-white p-3">
            <QRCodeSVG
              value={anamnesisUrl}
              size={120}
              level="M"
              fgColor="var(--foreground)"
              bgColor="transparent"
            />
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <h2 className="m-0 flex items-center gap-2 text-lg font-semibold">
              <ClipboardList aria-hidden className="size-6 shrink-0 text-primary" />
              {t("self-service.check-in.done.anamnesis-title")}
            </h2>
            <p className="m-0 text-base text-foreground">
              {t("self-service.check-in.done.anamnesis-description")}
            </p>
            <a
              href={anamnesisUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="done-anamnesis-link"
              className="block text-base font-medium break-all underline underline-offset-2"
            >
              {anamnesisUrl.replace(/^https?:\/\//, "")}
            </a>
          </div>
        </section>
      ) : null}
    </KioskStep>
  );
}
